import { useState, useRef, useEffect } from "react";
import { useTheme } from "../../../../context/ThemeContext";
import { Modal, Form, Button, Badge, Image, Spinner } from "react-bootstrap";
import { toast } from "sonner";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import "../../utils/quillPasteClipboard";
import { ReactComponent as ImageIcon } from "../../../../assets/images/upload-img.svg";
import { validateImageFile, normalizeNbsp } from "../../utils/communityHelpers";
import "../create-post-modal/CreatePostModal.scss";
import { FiX } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useCategories } from "../../hooks";
import {
  getPreviewLink,
  updatePost,
} from "../../../../redux/slices/editPostSlice";
import {
  activityEnd,
  activityStart,
} from "../../../../redux/slices/trackActivitySlice";
import Toast from "../../../../components/toasts";

const EditPost = ({ show, onHide, localPost }) => {
  const dispatch = useDispatch();
  const { resolvedTheme } = useTheme();
  const brokenImageFallback =
    resolvedTheme === "dark"
      ? "https://ui-avatars.com/api/?name=Image&background=1a2830&color=9aa7ac&size=60"
      : "https://ui-avatars.com/api/?name=Image&background=e0e0e0&color=999&size=60";

  // ============= STATE =============
  const [postContent, setPostContent] = useState("");
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [activeCategory, setActiveCategory] = useState(null);
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [removedImageIds, setRemovedImageIds] = useState([]);
  const [isPreviewPending, setIsPreviewPending] = useState(false);
  const [linkPreview, setLinkPreview] = useState(null);
  const [isPreviewLoading, setIsPreviewLoading] = useState(false);
  const [postTitle, setPostTitle] = useState("");

  // Poll state
  const [showPollForm, setShowPollForm] = useState(false);
  const [pollQuestion, setPollQuestion] = useState("");
  const [pollOptions, setPollOptions] = useState(["", ""]);
  const [isMultiSelect, setIsMultiSelect] = useState(false);

  const fileInputRef = useRef(null);
  const previewTimeoutRef = useRef(null);

  const { categories, loading: categoriesLoading } = useCategories();

  const editValidityTime = new Date(localPost?._metadata?.createdAt);
  const now = new Date();
  const diffMs = now - editValidityTime;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));

  // ============= URL HELPERS =============
  const extractUrl = (text) => {
    if (!text) return null;
    const urlRegex = /((https?:\/\/)?(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_+.~#?&//=]*))/gi;
    const match = text.match(urlRegex);
    if (!match || !match[0]) return null;
    let url = match[0].trim();
    if (!/^https?:\/\//i.test(url)) url = `https://${url}`;
    try {
      const parsedUrl = new URL(url);
      if (!parsedUrl.pathname || parsedUrl.pathname === "") parsedUrl.pathname = "/";
      return parsedUrl.toString();
    } catch {
      return null;
    }
  };

  const stripHtml = (html) => {
    if (!html) return "";
    const spaced = html
      .replace(/<br\s*\/?>/gi, " ")
      .replace(/<\/(p|div|li|h[1-6]|blockquote)>/gi, " ");
    const div = document.createElement("div");
    div.innerHTML = spaced;
    return div.textContent || "";
  };

  const isValidPreview = (preview) => {
    if (!preview) return false;
    const { title, description, image, domain } = preview;
    return Boolean(title?.trim()) || Boolean(description?.trim()) || Boolean(image?.trim()) || Boolean(domain?.trim());
  };

  // ============= ACTIVITY TRACKING =============
  useEffect(() => {
    dispatch(activityStart({ page_name: "EDIT_POST", platform: "web" }));
  }, [dispatch]);

  useEffect(() => {
    return () => { dispatch(activityEnd({ page_name: "EDIT_POST" })); };
  }, [dispatch]);

  // ============= LINK PREVIEW =============
  useEffect(() => {
    const url = extractUrl(stripHtml(postContent));
    if (!url) {
      setLinkPreview(null);
      setIsPreviewLoading(false);
      setIsPreviewPending(false);
      return;
    }
    setIsPreviewPending(true);
    if (previewTimeoutRef.current) clearTimeout(previewTimeoutRef.current);
    previewTimeoutRef.current = setTimeout(async () => {
      try {
        setIsPreviewLoading(true);
        const res = await dispatch(getPreviewLink({ url })).unwrap();
        setLinkPreview(res?.editPost || null);
      } catch {
        setLinkPreview(null);
      } finally {
        setIsPreviewLoading(false);
        setIsPreviewPending(false);
      }
    }, 2000);
    return () => clearTimeout(previewTimeoutRef.current);
  }, [postContent, dispatch]);

  // ============= INIT FROM localPost =============
  useEffect(() => {
    if (show && localPost) {
      setPostContent(localPost.content || "");
      setPostTitle(localPost.title || "");
      setIsAnonymous(localPost._metadata?.isAnonymous || 0);

      if (localPost.categoryId && categories && categories.length > 0) {
        const match = categories.find((cat) => cat.id === localPost.categoryId);
        if (match) setActiveCategory(match);
      }

      if (localPost.post_images && Array.isArray(localPost.post_images)) {
        const images = localPost.post_images.map((imgObj) => ({
          id: imgObj.id,
          url: `${process.env.REACT_APP_API_BASE_URL}/uploads/post_images/${imgObj.image_path}`,
        }));
        setExistingImages(images);
      }

      // Pre-fill poll if post has one
      if (localPost.poll) {
        setShowPollForm(true);
        setPollQuestion(localPost.poll.question || "");
        const opts = (localPost.poll.options || [])
          .sort((a, b) => a.display_order - b.display_order)
          .map((o) => ({ id: o.id, text: o.option_text || "" }));
        setPollOptions(opts.length >= 2 ? opts : [{ id: null, text: "" }, { id: null, text: "" }]);
        setIsMultiSelect(localPost.poll.is_multi_select || false);
      } else {
        setShowPollForm(false);
        setPollQuestion("");
        setPollOptions([{ id: null, text: "" }, { id: null, text: "" }]);
        setIsMultiSelect(false);
      }
    }
  }, [show, localPost, categories]);

  useEffect(() => {
    if (!show) resetForm();
  }, [show]);

  // ============= FILE HANDLERS =============
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    if (files.length === 0) return;
    const validFiles = [];
    const validPreviews = [];
    for (const file of files) {
      const validation = validateImageFile(file);
      if (!validation.valid) { toast.error(validation.error); continue; }
      validFiles.push(file);
      if (file.type.startsWith("image/")) {
        validPreviews.push(URL.createObjectURL(file));
      } else {
        validPreviews.push(null);
      }
    }
    if (validFiles.length > 0) {
      setSelectedFiles((prev) => [...prev, ...validFiles]);
      setPreviewUrls((prev) => [...prev, ...validPreviews]);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const removeExistingImage = (id) => {
    setExistingImages((prev) => prev.filter((img) => img.id !== id));
    setRemovedImageIds((prev) => [...prev, id]);
  };

  const clearFile = (index) => {
    if (previewUrls[index]) URL.revokeObjectURL(previewUrls[index]);
    setSelectedFiles((prev) => prev.filter((_, i) => i !== index));
    setPreviewUrls((prev) => prev.filter((_, i) => i !== index));
  };

  const clearAllFiles = () => {
    previewUrls.forEach((url) => { if (url) URL.revokeObjectURL(url); });
    setSelectedFiles([]);
    setPreviewUrls([]);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // ============= SUBMIT =============
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!postContent.trim()) { Toast.showError("Please enter post content"); return; }
    if (!postTitle?.trim()) { Toast.showError("Please enter post title"); return; }
    if (!activeCategory) { Toast.showError("Please select a category"); return; }

    // Poll validation
    if (showPollForm) {
      if (!pollQuestion.trim()) { Toast.showError("Please enter a poll question"); return; }
      const validPollOptions = pollOptions.filter((o) => (o.text || "").trim());
      if (validPollOptions.length < 2) { Toast.showError("Please enter at least 2 poll options"); return; }
    }

    try {
      setIsSubmitting(true);

      const formData = new FormData();
      formData.append("post_id", localPost.id);
      formData.append("title", postTitle || "");
      formData.append("content", normalizeNbsp(postContent).trim());
      formData.append("category", activeCategory?.id);
      formData.append("is_post_anonymously", isAnonymous ? 1 : 0);
      formData.append("link_preview", JSON.stringify({
        title: linkPreview?.title || "",
        description: linkPreview?.description || "",
        image: linkPreview?.image || "",
        domain: linkPreview?.domain || "",
      }));

      if (removedImageIds.length > 0) {
        formData.append("remove_images", removedImageIds.join(","));
      }
      selectedFiles.forEach((file) => { formData.append("post_images", file); });

      // Attach poll data — send {id, text} objects so backend can sync without losing votes
      if (showPollForm && pollQuestion.trim()) {
        const validPollOptions = pollOptions.filter((o) => (o.text || "").trim());
        if (validPollOptions.length >= 2) {
          formData.append("poll_question", pollQuestion.trim());
          formData.append("poll_options", JSON.stringify(
            validPollOptions.map((o) => o.id ? { id: o.id, text: o.text } : o.text)
          ));
          formData.append("poll_is_multi_select", isMultiSelect ? "true" : "false");
        }
      } else {
        // Send empty poll_question so backend knows poll was cleared/not set
        formData.append("poll_question", "");
      }

      await dispatch(updatePost(formData)).unwrap();
      resetForm();
      onHide();
      window.location.reload();
    } catch (error) {
      const msg =
        typeof error === "string"
          ? error
          : error?.response?.data?.data?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Failed to update post. Please try again.";
      toast.error(msg);
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============= RESET =============
  const resetForm = () => {
    setPostContent("");
    setPostTitle("");
    clearAllFiles();
    setIsAnonymous(false);
    setShowPollForm(false);
    setPollQuestion("");
    setPollOptions([{ id: null, text: "" }, { id: null, text: "" }]);
    setIsMultiSelect(false);
    setRemovedImageIds([]);
    if (categories && categories.length > 0) setActiveCategory(categories[0]);
  };

  const handleClose = () => {
    if (isSubmitting) return;
    resetForm();
    onHide();
  };

  const isFormValid =
    postContent?.trim() && activeCategory && !isSubmitting && !isPreviewLoading && !isPreviewPending;

  const displayCategories = categories || [];

  // ============= RENDER =============
  return (
    <Modal
      show={show}
      onHide={handleClose}
      className="create-post-modal"
      centered
      size="lg"
      backdrop={isSubmitting ? "static" : true}
      keyboard={!isSubmitting}
      dialogClassName="custom-modal"
    >
      <Modal.Header>
        <h5 className="modal-title">Edit post</h5>
        <Button className="close-btn" onClick={handleClose} disabled={isSubmitting}>
          <FiX size={24} />
        </Button>
      </Modal.Header>

      <Modal.Body className="pt-2">
        <Form onSubmit={handleSubmit}>
          {/* Category Selection */}
          <div className="category-section">
            <h6 className="category-title">Choose category</h6>
            {categoriesLoading ? (
              <div className="text-center py-2">
                <Spinner animation="border" size="sm" />
                <small className="ms-2">Loading categories...</small>
              </div>
            ) : (
              <div className="category-tabs">
                {displayCategories.map((category) => (
                  <Badge
                    key={category?.id}
                    bg={activeCategory?.id === category?.id ? "primary" : "light"}
                    text={activeCategory?.id === category?.id ? "white" : "dark"}
                    className={`category-tab ${activeCategory?.id === category?.id ? "active" : ""}`}
                    onClick={() => setActiveCategory(category)}
                    style={{ cursor: "pointer" }}
                  >
                    {category?.category_name}
                  </Badge>
                ))}
              </div>
            )}
          </div>

          {/* Post Title */}
          <Form.Group className="form-group">
            <Form.Label className="col-label">Post Title</Form.Label>
            <Form.Control
              type="text"
              placeholder="Enter Your title"
              className="col-input"
              value={postTitle}
              onChange={(e) => setPostTitle(e.target.value)}
              required
            />
          </Form.Group>

          {/* Post Content */}
          <div className="post-content-section">
            <div className="quill-wrapper">
              <ReactQuill
                theme="snow"
                value={postContent}
                onChange={setPostContent}
                readOnly={isSubmitting}
                placeholder="Share your experience..."
              />

              {/* Link Preview */}
              {isPreviewLoading && !isValidPreview(linkPreview) && (
                <div className="link-preview loading">
                  <Spinner animation="border" size="sm" />
                  <span className="ms-2">Fetching link preview...</span>
                </div>
              )}
              {!isPreviewLoading && isValidPreview(linkPreview) && (
                <div className="link-preview-card p-2 border rounded position-relative">
                  <button
                    type="button"
                    className="btn-close position-absolute top-0 end-0 m-2"
                    onClick={() => setLinkPreview(null)}
                  />
                  <div className="d-flex gap-2">
                    {linkPreview.image && (
                      <Image src={linkPreview.image} width={80} height={80} className="rounded" style={{ objectFit: "cover" }} />
                    )}
                    <div className="flex-grow-1">
                      {linkPreview.title && <h6 className="mb-1 text-truncate">{linkPreview.title}</h6>}
                      {linkPreview.description && <p className="mb-1 text-muted small line-clamp-2">{linkPreview.description}</p>}
                      {linkPreview.domain && <small className="text-primary">{linkPreview.domain}</small>}
                    </div>
                  </div>
                </div>
              )}

              {/* Image upload */}
              <div className="quill-footer">
                <button
                  type="button"
                  className="media-upload-btn"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={isSubmitting}
                  title="Upload image"
                >
                  <ImageIcon className="upload-icon" />
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/heic,image/heif"
                  onChange={handleFileSelect}
                  style={{ display: "none" }}
                  multiple
                  disabled={isSubmitting}
                />
              </div>
            </div>
          </div>

          {/* Edit time warning */}
          {diffMinutes > 180 && (
            <div className="text-center">
              <p style={{ color: "var(--mmp-danger)" }}>Post cannot be edited after 3 hours of creation</p>
            </div>
          )}

          {/* Existing Images */}
          {existingImages.length > 0 && (
            <div className="existing-images-container mb-3">
              <small className="text-muted d-block mb-2">Existing Images:</small>
              {existingImages.map((image, index) => (
                <div key={`existing-${index}`} className="file-preview mb-2 p-2 bg-light rounded d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center flex-grow-1">
                    <Image
                      key={resolvedTheme}
                      src={image.url}
                      width={60}
                      height={60}
                      className="me-3 rounded"
                      style={{ objectFit: "cover" }}
                      alt={`Existing ${index + 1}`}
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = brokenImageFallback;
                      }}
                    />
                    <div className="flex-grow-1">
                      <div className="file-title text-truncate">Existing Image {index + 1}</div>
                    </div>
                  </div>
                  <Button variant="outline-danger" size="sm" onClick={() => removeExistingImage(image.id)} disabled={isSubmitting}>×</Button>
                </div>
              ))}
            </div>
          )}

          {/* New File Previews */}
          {selectedFiles.length > 0 && (
            <div className="files-preview-container mb-2">
              <small className="text-muted d-block mb-2">New Images:</small>
              {selectedFiles.map((file, index) => (
                <div key={`file-${index}`} className="file-preview mb-2 p-2 bg-light rounded d-flex align-items-center justify-content-between">
                  <div className="d-flex align-items-center flex-grow-1">
                    {previewUrls[index] && (
                      <Image src={previewUrls[index]} width={60} height={60} className="me-3 rounded" style={{ objectFit: "cover" }} alt={`Preview ${index + 1}`} />
                    )}
                    <div className="flex-grow-1">
                      <div className="file-title text-truncate" style={{ maxWidth: "300px" }}>{file.name}</div>
                      <small className="file-size text-muted">{(file.size / 1024 / 1024).toFixed(2)} MB</small>
                    </div>
                  </div>
                  <Button variant="outline-danger" size="sm" onClick={() => clearFile(index)} disabled={isSubmitting}>×</Button>
                </div>
              ))}
            </div>
          )}

          {/* Character Count */}
          {postContent && (
            <div className="post-count text-end mb-2">
              <small className="text-muted">{postContent.length} / 5000</small>
            </div>
          )}

          {/* Poll Toggle Checkbox */}
          <div className="poll-checkbox-toggle">
            <label className="poll-checkbox-label">
              <input
                type="checkbox"
                checked={showPollForm}
                onChange={(e) => {
                  setShowPollForm(e.target.checked);
                  if (!e.target.checked) {
                    setPollQuestion("");
                    setPollOptions([{ id: null, text: "" }, { id: null, text: "" }]);
                    setIsMultiSelect(false);
                  }
                }}
                disabled={isSubmitting}
                className="poll-checkbox-input"
              />
              <span className="poll-checkbox-text">Add a poll</span>
            </label>
          </div>

          {/* Poll Section */}
          {showPollForm && (
            <div className="poll-form-section">
              <div className="poll-form-header">
                <div className="poll-form-title">
                  <span>Poll</span>
                </div>
                <button
                  type="button"
                  className="poll-close-btn"
                  aria-label="Remove poll"
                  onClick={() => {
                    setShowPollForm(false);
                    setPollQuestion("");
                    setPollOptions([{ id: null, text: "" }, { id: null, text: "" }]);
                    setIsMultiSelect(false);
                  }}
                >
                  <FiX size={15} />
                </button>
              </div>
              <div className="poll-form-body">
                <Form.Control
                  type="text"
                  placeholder="Ask your question..."
                  value={pollQuestion}
                  onChange={(e) => setPollQuestion(e.target.value)}
                  maxLength={300}
                  className="poll-question-input"
                  disabled={isSubmitting}
                />
                <div className="poll-options-list">
                  {pollOptions.map((opt, idx) => (
                    <div key={idx} className="poll-option-row">
                      <div className="option-number">{idx + 1}</div>
                      <Form.Control
                        type="text"
                        placeholder={`Option ${idx + 1}`}
                        value={opt.text}
                        onChange={(e) => {
                          const updated = [...pollOptions];
                          updated[idx] = { ...updated[idx], text: e.target.value };
                          setPollOptions(updated);
                        }}
                        maxLength={100}
                        className="poll-option-input"
                        disabled={isSubmitting}
                      />
                      {pollOptions.length > 2 && (
                        <button
                          type="button"
                          className="option-remove-btn"
                          onClick={() => setPollOptions(pollOptions.filter((_, i) => i !== idx))}
                          disabled={isSubmitting}
                        >
                          <FiX size={14} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
                {pollOptions.length < 6 && (
                  <button
                    type="button"
                    className="add-option-btn"
                    onClick={() => setPollOptions([...pollOptions, { id: null, text: "" }])}
                    disabled={isSubmitting}
                  >
                    + Add Option
                  </button>
                )}
                <div className="poll-multi-toggle">
                  <label className="poll-multi-label">
                    <input
                      type="checkbox"
                      checked={isMultiSelect}
                      onChange={(e) => setIsMultiSelect(e.target.checked)}
                      disabled={isSubmitting}
                      className="poll-multi-checkbox"
                    />
                    Allow multiple selections
                  </label>
                </div>
              </div>
            </div>
          )}

          {/* Action Buttons */}
          <div className="action-buttons d-flex justify-content-between align-items-center gap-3 mt-3">
            <div className="d-flex gap-3">
              <Button
                variant={isAnonymous ? "primary" : "outline-primary"}
                className="anonymous-btn"
                onClick={() => setIsAnonymous(!isAnonymous)}
                disabled={isSubmitting}
              >
                {isAnonymous ? "✓ " : ""}Post Anonymously
              </Button>
            </div>
            <Button
              type="submit"
              variant="primary"
              className="post-btn"
              disabled={!isFormValid}
            >
              {isSubmitting ? (
                <>
                  <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                  Saving...
                </>
              ) : (
                "Save"
              )}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default EditPost;
